import { useCameraParametersIfAvailable } from "@/components/r3f/utils/camera-parameters";
import {
  ModeTransitionProps,
  SceneFilterLookAtInitialState,
} from "@/modes/mode";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import {
  selectCanRenderSceneFilter,
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { useEffect, useLayoutEffect } from "react";
import { useActiveCameraFromDeepLink } from "../overview-mode/overview-transition";
import { selectWaypointTargetSceneFilter } from "../walk-mode/walk-scene-selectors";
import { useComputeValidWalkScene } from "../walk-mode/walk-state";
import { sceneFilterFromElement } from "../walk-mode/walk-types";
import { isFlyWalkImageElement } from "./flywalk-types";

/**
 * @returns the transition to enter FlyWalkMode
 */
export function FlyWalkTransition({
  initialState,
  onCompleted,
  modeCamera,
}: ModeTransitionProps<SceneFilterLookAtInitialState>): null {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  // Setup scene filter from a deep link if available
  useLayoutEffect(() => {
    if (initialState?.scene) {
      dispatch(setFlyWalkSceneFilter(initialState.scene));
    }
  }, [dispatch, initialState]);

  // A deep link can contain an ortho camera to a 2D photo: let's account for it
  const { validCamera, completedCallback } = useActiveCameraFromDeepLink(
    modeCamera,
    onCompleted,
    initialState,
  );

  // Setup camera from a deep link if available
  useCameraParametersIfAvailable(
    validCamera,
    initialState?.camera,
    completedCallback,
  );

  // Without this hook, the user could go to overview mode with a point cloud, click "walk mode"
  // and the flywalk mode overlay would not be mounted because the flywalk main element
  // would be undefined, since the active element is the dataset.
  const main = useComputeValidWalkScene();

  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const scene = useCurrentScene();
  const area = useCurrentArea();

  // Checked against the same selectors used to render the scene, so a session that is
  // e.g. a Rovi scan (bucketed under dataSessions5d, not dataSessions3d) is still found.
  const canRenderSceneFilter = useAppSelector(
    selectCanRenderSceneFilter("walk", scene, area),
  );

  // This hook fixes a bug where the user could click on a waypoint in sheet mode
  // and enter the flywalk mode with a pano image while the scene filter potentially being
  // set to 'PointCloud' or something else.
  // Furthermore, this hook makes flywalk mode stable against projects without panos, e.g. ones
  // with only a CAD model, or Rovi scans made of a trajectory, a point cloud and a Gaussian splat.
  // Keep the user's chosen filter when it is still valid; only derive a fallback from `main`
  // when the current filter can no longer render in this scene.
  useEffect(() => {
    // The overlay filter combines a point cloud and a CAD, so it is never derived from a single element.
    if (initialState || !main || sceneFilter === SceneFilter.Overlay) return;
    if (canRenderSceneFilter) return;

    // For an image, a Rovi-scan waypoint resolves to the scan's Gaussian splat when available.
    const targetFilter = isFlyWalkImageElement(main)
      ? selectWaypointTargetSceneFilter(main)(store.getState())
      : sceneFilterFromElement(main);
    dispatch(setFlyWalkSceneFilter(targetFilter));
  }, [initialState, dispatch, main, store, sceneFilter, canRenderSceneFilter]);

  // If no camera parameters were provided in a deep link, end the transition
  useEffect(() => {
    if (!initialState?.camera) {
      onCompleted();
    }
  }, [initialState?.camera, onCompleted]);

  return null;
}
